// Admin full-page Settings shell (Volkan direction 2026-06-21).
//
// Replaces the old centered-modal Settings for the ADMIN surface only. Clicking
// the gear "Settings" entry in the main admin sidebar opens this full-screen
// overlay: the left rail becomes a dedicated Settings navigation, the right pane
// renders the selected section. A "Back to Dashboard" button at the very top
// returns to /admin (overlay unmounts → main admin sidebar reappears).
//
// Seller/supply keep the shared modal Settings (features/settings/Settings.tsx)
// untouched — D-7 admin-only change.
//
// Rail (2026-10-06, admin menu simplification part 3): related sections share
// one rail entry and switch with a tab strip, the rest stay single entries.
//   COMPANY    — Organization · Users · Brand [Login screen | Catalog PDF brand]
//   OPERATIONS — Shipping [5 tabs] · Integrations [Marketplace apps | Payments |
//                Media generation | Research tools | Etsy listing push] · Partner access [API keys | Catalogue
//                sharing] · Referral Program · Policies · Review Tracking
//   SYSTEM     — Notifications · Security · Feature switches [4 tabs] ·
//                Appearance (flagged) · Drive Maintenance
//   HELP       — Help [Demo guide | Resources]
// My Permissions moved to the account page (a tab there) and Languages left —
// the account page's Profile already sets the language. Their old ?section=
// links continue to /admin/account.
//
// 2026-10-05 menu simplification: Policies, Payment Gateways, Review Tracking,
// Catalog Brand (Document Studio brand profile) and Drive Maintenance moved in
// from the main sidebar — set-and-forget screens. Employee Tracking (its Save
// stored nothing) and Fulfillment Ecosystems (a fixed four-row list) left the
// rail; their old deep links land on Organization.
//
// Operational queues (Onboarding, Self-Apply) and the Create-Label tools stay in
// the main sidebar — they are daily operations, not configuration.

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import {
  ArrowLeft,
  Bell,
  Building2,
  FileText,
  Plug,
  KeyRound,
  Palette,
  Search,
  Star,
  ToggleRight,
  Wrench,
  ShieldCheck,
  Truck,
  UserCog,
  BookOpen,
  Handshake,
  type LucideIcon,
} from 'lucide-react'

import { Organization } from '../settings/Organization'
import { TagManagement } from '../settings/TagManagement'
import { PrintingSetup } from '../settings/PrintingSetup'
import { Notifications } from '../settings/Notifications'
import { Security } from '../settings/Security'
import { ViewAsControl } from '../settings/ViewAsControl'
import { ViewAsOrgOverrides } from '../settings/ViewAsOrgOverrides'
import { Appearance } from '../settings/Appearance'
import { ShippingHub } from './shipping/ShippingHub'
import { InternationalSettings } from './shipping/InternationalSettings'
import { ShipFromLocationsPage } from './shipping/ShipFromLocationsPage'
import { MarketplaceApps } from './MarketplaceApps'
import { MediaGeneration } from './MediaGeneration'
import { ResearchTools } from './ResearchTools'
import { EtsyListingPushSettings } from './etsy-listing/EtsyListingPushSettings'
import { PartnerApiKeys } from './PartnerApiKeys'
import { PartnerCatalogAccess } from './PartnerCatalogAccess'
import { ReferralProgramAdmin } from './ReferralProgramAdmin'
import { AdminUsersPage } from './AdminUsersPage'
import { DemoGuide } from './DemoGuide'
import { ResourcesPage } from '../shared/ResourcesPage'
import { MessagingRolloutControl } from '../settings/MessagingRolloutControl'
import { ProblemReportingRolloutControl } from '../settings/ProblemReportingRolloutControl'
import { InventoryRolloutControl } from '../settings/InventoryRolloutControl'
import { fetchMe } from '../../lib/auth'
import { USE_REAL_API } from '../../lib/api'
import { APPEARANCE_ENABLED } from '../../store/appearance'
import { LoginExperienceSettings } from './LoginExperienceSettings'
import { PaymentGateways } from './PaymentGateways'
import { PoliciesPage } from './Policies/PoliciesPage'
import { ReviewTrackingSettings } from './ReviewTrackingSettings'
import { BrandSettingsPage } from './document-studio/BrandSettingsPage'
import { DriveMaintenancePage } from './DriveMaintenancePage'
import { SectionTabs } from '../../components/ui/route-tabs'
import {
  movedAdminSettingsSectionTarget,
  resolveAdminSettingsSection,
  type AdminSettingsSectionId as SectionId,
} from './admin-settings-deeplink'
import { useT } from '../../i18n'
import {
  adminSettingsShellI18n,
  type AdminSettingsShellStrings,
} from '../../i18n/admin/settings-shell'

type SectionDef = {
  id: SectionId
  label: string
}

// One rail entry. A single-section entry has one tab and shows no strip; a
// multi-section entry shows its sections as tabs above the content.
type RailItem = {
  id: string
  label: string
  icon: LucideIcon
  tabs: SectionDef[]
}

type RailGroup = {
  id: string
  label: string
  items: RailItem[]
}

const single = (id: SectionId, label: string, icon: LucideIcon): RailItem => ({
  id,
  label,
  icon,
  tabs: [{ id, label }],
})

const buildGroups = (t: AdminSettingsShellStrings): RailGroup[] => [
  {
    id: 'company',
    label: t.groups.company,
    items: [
      single('organization', t.items.organization, Building2),
      single('users', t.items.users, UserCog),
      {
        id: 'brand',
        label: t.items.brand,
        icon: Palette,
        tabs: [
          { id: 'login-experience', label: t.tabs.loginExperience },
          { id: 'catalog-brand', label: t.tabs.catalogBrand },
        ],
      },
    ],
  },
  {
    id: 'operations',
    label: t.groups.operations,
    items: [
      {
        id: 'shipping',
        label: t.items.shipping,
        icon: Truck,
        tabs: [
          { id: 'shipping-accounts', label: t.tabs.shippingAccounts },
          { id: 'shipping-international', label: t.tabs.shippingInternational },
          { id: 'shipping-locations', label: t.tabs.shippingLocations },
          { id: 'shipping-tags', label: t.tabs.shippingTags },
          { id: 'shipping-printing', label: t.tabs.shippingPrinting },
        ],
      },
      {
        id: 'integrations',
        label: t.items.integrations,
        icon: Plug,
        tabs: [
          { id: 'marketplace-apps', label: t.tabs.marketplaceApps },
          { id: 'payment-gateways', label: t.tabs.paymentGateways },
          { id: 'media-generation', label: t.tabs.mediaGeneration },
          { id: 'research-tools', label: t.tabs.researchTools },
          { id: 'etsy-listing', label: t.tabs.etsyListing },
        ],
      },
      {
        id: 'partner-access',
        label: t.items.partnerAccess,
        icon: KeyRound,
        tabs: [
          { id: 'partner-api', label: t.tabs.partnerApi },
          { id: 'partner-catalog', label: t.tabs.partnerCatalog },
        ],
      },
      single('referral-program', t.items.referralProgram, Handshake),
      single('policies', t.items.policies, FileText),
      single('review-tracking', t.items.reviewTracking, Star),
    ],
  },
  {
    id: 'system',
    label: t.groups.system,
    items: [
      single('notifications', t.items.notifications, Bell),
      single('security', t.items.security, ShieldCheck),
      {
        id: 'feature-switches',
        label: t.items.featureSwitches,
        icon: ToggleRight,
        tabs: [
          { id: 'messaging', label: t.tabs.messaging },
          { id: 'problem-reporting', label: t.tabs.problemReporting },
          { id: 'inventory-rollout', label: t.tabs.inventoryRollout },
          { id: 'view-as', label: t.tabs.viewAs },
        ],
      },
      single('appearance', t.items.appearance, Palette),
      single('drive-maintenance', t.items.driveMaintenance, Wrench),
    ],
  },
  {
    id: 'help',
    label: t.groups.help,
    items: [
      {
        id: 'help',
        label: t.items.help,
        icon: BookOpen,
        tabs: [
          { id: 'demo-guide', label: t.tabs.demoGuide },
          { id: 'resources', label: t.tabs.resources },
        ],
      },
    ],
  },
]

// Rail filtered by flags, permissions and the search box. A match on any tab
// keeps its whole entry (all its tabs) so the strip never shows half a set.
function visibleGroups(
  groupsDef: RailGroup[],
  query: string,
  canManageProblemReporting: boolean,
): RailGroup[] {
  const q = query.trim().toLowerCase()
  return groupsDef.map((group) => ({
    ...group,
    items: group.items
      .map((item) => ({
        ...item,
        tabs: item.tabs.filter(
          (tab) =>
            (tab.id !== 'appearance' || APPEARANCE_ENABLED) &&
            (tab.id !== 'problem-reporting' || canManageProblemReporting),
        ),
      }))
      .filter((item) => item.tabs.length > 0)
      .filter(
        (item) =>
          !q ||
          group.label.toLowerCase().includes(q) ||
          item.label.toLowerCase().includes(q) ||
          item.tabs.some((tab) => tab.label.toLowerCase().includes(q)),
      ),
  })).filter((group) => group.items.length > 0)
}

function SectionContent({ active }: { active: SectionId }) {
  switch (active) {
    case 'organization':
      return <Organization surface="admin" />
    case 'shipping-locations':
      return <ShipFromLocationsPage />
    case 'shipping-international':
      return <InternationalSettings />
    case 'shipping-accounts':
      return <ShippingHub />
    case 'shipping-tags':
      return <TagManagement surface="admin" />
    case 'shipping-printing':
      return <PrintingSetup surface="admin" />
    case 'payment-gateways':
      return <PaymentGateways />
    case 'marketplace-apps':
      return <MarketplaceApps />
    case 'media-generation':
      return <MediaGeneration />
    case 'research-tools':
      return <ResearchTools />
    case 'etsy-listing':
      return <EtsyListingPushSettings />
    case 'partner-api':
      return <PartnerApiKeys />
    case 'partner-catalog':
      return <PartnerCatalogAccess />
    case 'referral-program':
      return <ReferralProgramAdmin />
    case 'login-experience':
      return <LoginExperienceSettings />
    case 'catalog-brand':
      return <BrandSettingsPage />
    case 'policies':
      return <PoliciesPage />
    case 'review-tracking':
      return <ReviewTrackingSettings />
    case 'users':
      return <AdminUsersPage />
    case 'notifications':
      return <Notifications surface="admin" />
    case 'messaging':
      return <MessagingRolloutControl />
    case 'problem-reporting':
      return <ProblemReportingRolloutControl />
    case 'inventory-rollout':
      return <InventoryRolloutControl />
    case 'view-as':
      return (
        <>
          <ViewAsControl />
          <ViewAsOrgOverrides />
        </>
      )
    case 'security':
      return <Security surface="admin" />
    case 'appearance':
      return APPEARANCE_ENABLED ? <Appearance /> : null
    case 'drive-maintenance':
      return <DriveMaintenancePage />
    case 'demo-guide':
      return <DemoGuide />
    case 'resources':
      return <ResourcesPage role="admin" />
    default:
      return null
  }
}

export function AdminSettingsShell() {
  const router = useRouter()
  const t = useT(adminSettingsShellI18n)
  const groupsDef = useMemo(() => buildGroups(t), [t])
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const canManageProblemReporting = !USE_REAL_API ||
    Boolean(meQuery.data?.capabilities.includes('admin_config.manage'))
  const [query, setQuery] = useState('')
  const [active, setActive] = useState<SectionId>(() =>
    (() => {
      const section = resolveAdminSettingsSection(window.location.search)
      return section === 'appearance' && !APPEARANCE_ENABLED ? 'organization' : section
    })(),
  )

  const groups = useMemo(
    () => visibleGroups(groupsDef, query, canManageProblemReporting),
    [groupsDef, query, canManageProblemReporting],
  )
  const mobileGroups = useMemo(
    () => visibleGroups(groupsDef, '', canManageProblemReporting),
    [groupsDef, canManageProblemReporting],
  )
  const activeItem = groups
    .flatMap((group) => group.items)
    .find((item) => item.tabs.some((tab) => tab.id === active))

  // Keep active pointing at a visible section when a query or permission
  // hides it (adjust-during-render; converges once active is visible again).
  if ((!USE_REAL_API || !meQuery.isPending) && groups.length > 0 && !activeItem) {
    setActive(groups[0].items[0].tabs[0].id)
  }

  const back = () => void router.navigate({ to: '/admin' })

  // Supplier Hub and Drive Audit left Settings for main-menu pages
  // (2026-10-05); old ?section= links continue to their new address.
  useEffect(() => {
    const target = movedAdminSettingsSectionTarget(window.location.search)
    if (target) void router.navigate({ to: target, replace: true })
  }, [router])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex overflow-hidden bg-[var(--color-canvas)] text-[var(--color-text)]">
      {/* Settings rail */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] sm:flex">
        <div className="border-b border-[var(--color-border)] p-3">
          <button
            type="button"
            onClick={back}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:shadow-[var(--shadow-focus)]"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
            {t.backToDashboard}
          </button>
        </div>

        <div className="px-3 pb-2 pt-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.settings}
        </div>

        <div className="px-3 pb-2">
          <label className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-[var(--color-text-muted)]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-canvas)] px-8 py-1.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-focus)] focus:outline-none focus:shadow-[var(--shadow-focus)]"
            />
          </label>
        </div>

        <nav aria-label={t.navLabel} className="flex-1 overflow-y-auto px-2 pb-16">
          {groups.length === 0 ? (
            <div className="px-3 py-6 text-xs text-[var(--color-text-muted)]">{t.noMatches(query)}</div>
          ) : (
            groups.map((g) => (
              <div key={g.id} className="mb-4">
                <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  {g.label}
                </div>
                <div className="flex flex-col gap-0.5">
                  {g.items.map((item) => {
                    const Icon = item.icon
                    const isActive = item === activeItem
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          if (!isActive) setActive(item.tabs[0].id)
                        }}
                        className={
                          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors ' +
                          (isActive
                            ? 'bg-[var(--color-selected)] font-medium text-[var(--color-text)]'
                            : 'text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]')
                        }
                      >
                        <Icon
                          className={
                            'h-4 w-4 shrink-0 ' +
                            (isActive ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]')
                          }
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </nav>
      </aside>

      {/* Content */}
      <main className="min-w-0 flex-1 overflow-y-auto bg-[var(--color-canvas)] p-4 sm:p-6">
        <div className="mb-4 flex items-center gap-3 border-b border-[var(--color-border)] pb-4 sm:hidden">
          <button
            type="button"
            onClick={back}
            aria-label={t.backToDashboard}
            className="shrink-0 rounded-md border border-[var(--color-border)] p-2 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <select
            aria-label={t.sectionPickerLabel}
            value={active}
            onChange={(event) => {
              setQuery('')
              setActive(event.target.value as SectionId)
            }}
            className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-focus)] focus:outline-none focus:shadow-[var(--shadow-focus)]"
          >
            {mobileGroups.map((group) => (
              <optgroup key={group.id} label={group.label}>
                {group.items.flatMap((item) => item.tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {item.tabs.length > 1 ? `${item.label} · ${tab.label}` : item.label}
                  </option>
                )))}
              </optgroup>
            ))}
          </select>
        </div>
        {activeItem && activeItem.tabs.length > 1 && (
          <div className="hidden sm:block">
            <SectionTabs
              title={activeItem.label}
              tabs={activeItem.tabs}
              activeId={active}
              onSelect={(id) => setActive(id as SectionId)}
            />
          </div>
        )}
        {active !== 'problem-reporting' || canManageProblemReporting
          ? <SectionContent active={active} />
          : null}
      </main>
    </div>
  )
}
